import type { ErrorKind } from "@/services/client";

// The shell's loading and failure UI. Every state is distinct, honest,
// and token-themed — never a blank frame or an infinite spinner. Deliberately
// unbranded: this surface renders inside vendor pages.

const COPY: Record<ErrorKind, { title: string; detail: string }> = {
  "unknown-type": {
    title: "This document can't be displayed",
    detail: "The link points to a document type this viewer doesn't support.",
  },
  "handshake-timeout": {
    title: "Connection not established",
    detail:
      "The embedding page didn't provide credentials. Reload the page to try again.",
  },
  entitlement: {
    title: "You don't have access to this document",
    detail: "The provided credentials aren't entitled to view this content.",
  },
  "not-found": {
    title: "Document not found",
    detail: "This document doesn't exist or is no longer available.",
  },
  network: {
    title: "Something went wrong",
    detail: "The document couldn't be loaded. Reload the page to try again.",
  },
};

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-2 bg-viewer-bg px-6 text-center font-viewer text-viewer-fg">
      {children}
    </div>
  );
}

export function ErrorState({ kind }: { kind: ErrorKind }) {
  const { title, detail } = COPY[kind];
  return (
    <Centered>
      <p className="m-0 text-base font-semibold" data-error-kind={kind}>
        {title}
      </p>
      <p className="m-0 text-sm text-viewer-muted">{detail}</p>
    </Centered>
  );
}

export function Loading() {
  return (
    <Centered>
      <span
        role="status"
        aria-label="Loading"
        className="size-5 animate-spin rounded-full border-2 border-viewer-border border-t-viewer-accent"
      />
    </Centered>
  );
}
